/**
 * ProfileInfoSection — User info display (name, email), edit name dialog
 */

import React, { useState, useEffect } from 'react';
import { TouchableOpacity } from 'react-native';
import { VStack, HStack, Text } from '@gluestack-ui/themed';
import { useTranslation } from 'react-i18next';
import { useDisplayTheme } from '../../hooks/useDisplayTheme';
import { colors, FS_XS, FS_SM, FS_MD } from '../../theme';
import { SectionCard } from '../ui';
import { useAuthStore } from '../../store/authStore';
import { useUserPreferencesStore } from '../../store/userPreferencesStore';
import { selectDisplayName, selectUpdateDisplayName } from '../../store/userPreferencesSelectors';
import { PromptDialog } from '../ui/PromptDialog';

export const ProfileInfoSection = React.memo(function ProfileInfoSection() {
  const { theme, fontScale, ffs } = useDisplayTheme();
  const { t } = useTranslation();

  const user = useAuthStore((s) => s.user);
  const displayName = useUserPreferencesStore(selectDisplayName);
  const updateDisplayName = useUserPreferencesStore(selectUpdateDisplayName);

  const [name, setName] = useState(displayName || '');
  const [showNamePrompt, setShowNamePrompt] = useState(false);

  // Intentional: sync local name state when store displayName changes
  /* eslint-disable react-hooks/set-state-in-effect */
  useEffect(() => {
    setName(displayName || '');
  }, [displayName]);
  /* eslint-enable react-hooks/set-state-in-effect */

  return (
    <>
      <SectionCard>
        <Text allowFontScaling={false} accessibilityRole="header" style={{ fontSize: FS_MD * fontScale, fontWeight: 'bold', color: theme.text, ...ffs }}>
          {t('profile.info.title')}
        </Text>

        <HStack justifyContent="space-between" alignItems="center">
          <VStack>
            <Text allowFontScaling={false} style={{ fontSize: FS_XS * fontScale, color: theme.textSecondary, ...ffs }}>
              {t('profile.info.nameLabel')}
            </Text>
            <Text allowFontScaling={false} testID="profile-display-name" style={{ fontSize: FS_SM * fontScale, color: theme.text, ...ffs }}>
              {displayName || t('profile.info.namePlaceholder')}
            </Text>
          </VStack>
          <TouchableOpacity
            testID="profile-edit-name"
            accessibilityLabel={t('profile.info.nameLabel')}
            accessibilityRole="button"
            onPress={() => setShowNamePrompt(true)}
            hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
          >
            <Text allowFontScaling={false} style={{ fontSize: FS_SM * fontScale, color: colors.primary, fontWeight: '600', ...ffs }}>
              {t('common.update')}
            </Text>
          </TouchableOpacity>
        </HStack>

        <VStack>
          <Text allowFontScaling={false} style={{ fontSize: FS_XS * fontScale, color: theme.textSecondary, ...ffs }}>
            {t('common.email')}
          </Text>
          <Text allowFontScaling={false} style={{ fontSize: FS_SM * fontScale, color: theme.text, ...ffs }}>
            {user?.email}
          </Text>
        </VStack>
      </SectionCard>

      <PromptDialog
        visible={showNamePrompt}
        title={t('profile.info.nameLabel')}
        message={t('profile.info.nameHint')}
        defaultValue={name}
        placeholder={t('profile.info.namePlaceholder')}
        confirmLabel={t('common.save')}
        cancelLabel={t('common.cancel')}
        maxLength={50}
        onConfirm={(value) => {
          setShowNamePrompt(false);
          if (value !== displayName) {
            updateDisplayName(value);
            setName(value);
          }
        }}
        onCancel={() => setShowNamePrompt(false)}
      />
    </>
  );
});
